import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hook for the root system-health probe (`GET /health`,
 * `@Public` + `@SkipThrottle`). Renders the dashboard's system-health
 * tile — overall status + the three downstream service probes + process
 * uptime.
 *
 * The response shape is NOT in `@carcheck/shared` (the api's
 * `HealthResponse` is api-internal infrastructure, never a wire DTO in
 * shared), so the contract is mirrored locally here. It maps 1:1 to
 * `packages/api/src/infrastructure/health/health.controller.ts` — the
 * tile renders ONLY what the payload carries (db / redis / s3 + uptime +
 * overall status); it invents no BullMQ / WebPay / Sumsub rows and no
 * latency figures (none are in the payload).
 *
 * Query-key convention (ADMIN-10 guardrail): `['admin', 'health']`.
 * `refetchInterval` keeps the tile live alongside the queue-counts poll.
 */

const HEALTH_BASE = '/health';

/** Per-service probe outcome (mirrors the api `ServiceStatus`). */
export type AdminHealthServiceStatus = 'ok' | 'down';

/** System-health payload (mirrors the api `HealthResponse`). */
export interface AdminHealthDto {
  status: 'ok' | 'degraded';
  services: {
    db: AdminHealthServiceStatus;
    redis: AdminHealthServiceStatus;
    s3: AdminHealthServiceStatus;
  };
  /** Process uptime in seconds (`process.uptime()`). */
  uptime: number;
}

/** Stable query-key for the health read. */
export const ADMIN_HEALTH_KEY = ['admin', 'health'] as const;

/** Poll cadence for the live health tile (ms). */
export const HEALTH_REFETCH_MS = 30_000;

export const useAdminHealth = (): UseQueryResult<AdminHealthDto, Error> =>
  useQuery({
    queryKey: ADMIN_HEALTH_KEY,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminHealthDto>(HEALTH_BASE, { signal });
      return data;
    },
    refetchInterval: HEALTH_REFETCH_MS,
  });
